/**
 * Intervention API service.
 *
 * Talks to the real /interventions backend: listing, manual creation,
 * progress updates, and aggregate analytics. AI-generated recommendations
 * are not implemented server-side (they need a deployed predictive model),
 * so no recommendation calls exist here.
 */

import { get, post, put } from './apiClient';

export type InterventionStatus = 'active' | 'completed' | 'discontinued';

export type EffectivenessRating =
  | 'effective'
  | 'partially_effective'
  | 'not_effective';

export interface ProgressEntry {
  date: string;
  note: string;
  recorded_by: string;
}

export interface Intervention {
  id: number;
  student_id: number;
  student_name: string;
  intervention_name: string;
  intervention_type: string;
  tier_level: number;
  start_date: string | null;
  end_date: string | null;
  frequency: string | null;
  duration_minutes: number | null;
  provider: string;
  location: string | null;
  target_goals: string[];
  success_criteria: string[];
  progress_data: ProgressEntry[];
  effectiveness_rating: EffectivenessRating | null;
  status: InterventionStatus;
  notes: string | null;
  discontinuation_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InterventionAnalytics {
  summary: {
    total_interventions: number;
    active_interventions: number;
    completed_interventions: number;
    completion_rate: number;
    rated_interventions: number;
    effective_rate: number | null;
    students_with_interventions: number;
  };
  interventions_by_type: Record<string, number>;
  interventions_by_tier: Record<string, number>;
  interventions_by_status: Record<string, number>;
}

export interface StudentOption {
  id: number;
  name: string;
  grade_level: string;
  district_student_id: string;
  school_name: string;
}

export interface InterventionCreateInput {
  student_id: number;
  intervention_name: string;
  intervention_type: string;
  tier_level: number;
  start_date: string;
  end_date?: string;
  frequency?: string;
  duration_minutes?: number;
  provider: string;
  location?: string;
  target_goals: string[];
  success_criteria: string[];
  notes?: string;
}

export interface InterventionProgressInput {
  status?: InterventionStatus;
  progress_note?: string;
  effectiveness_rating?: EffectivenessRating;
  end_date?: string;
  discontinuation_reason?: string;
}

export interface InterventionFilters {
  student_id?: number;
  status?: string;
  intervention_type?: string;
  tier_level?: number;
}

const BASE = '/interventions';

export function listInterventions(
  filters: InterventionFilters = {}
): Promise<Intervention[]> {
  const params = new URLSearchParams();
  if (filters.student_id !== undefined)
    params.set('student_id', String(filters.student_id));
  if (filters.status) params.set('status', filters.status);
  if (filters.intervention_type)
    params.set('intervention_type', filters.intervention_type);
  if (filters.tier_level !== undefined)
    params.set('tier_level', String(filters.tier_level));
  const query = params.toString();
  return get<Intervention[]>(query ? `${BASE}?${query}` : BASE);
}

export function getInterventionAnalytics(): Promise<InterventionAnalytics> {
  return get<InterventionAnalytics>(`${BASE}/analytics`);
}

export function listStudentOptions(): Promise<StudentOption[]> {
  return get<StudentOption[]>(`${BASE}/students`);
}

export function createIntervention(
  input: InterventionCreateInput
): Promise<Intervention> {
  return post<Intervention>(BASE, input);
}

export function updateInterventionProgress(
  interventionId: number,
  input: InterventionProgressInput
): Promise<Intervention> {
  return put<Intervention>(`${BASE}/${interventionId}/progress`, input);
}
